import { truncateLength } from "@/components/app-layout/consts";
import { useGlobalUsersData, useUsersData } from "@/data/use-users-data";
import { GetUsersDto } from "@/lib/gen-api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAdd } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useMemo, useState } from "react";
import { GroupedTable } from "../GroupedTable/GroupedTable";
import { UserDisplayName } from "../display/UserDisplayName";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Input } from "../ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

export function LinkUserToGroupDialog({
  groupUuid,
}: {
  groupUuid: string | null | undefined;
}) {
  const { users, isLoading: globalUsersLoading } = useGlobalUsersData();
  const {
    users: groupUsers,
    isLoading: groupUsersLoading,
    linkUser,
  } = useUsersData(groupUuid ?? null);

  const [search, setSearch] = useState("");

  const isLoading = !groupUuid || globalUsersLoading || groupUsersLoading;

  const availableUsers = useMemo(() => {
    return users.filter(
      (user) => !groupUsers.some((u) => u.uuid === user.uuid),
    );
  }, [users, groupUsers]);

  const filteredUsers = useMemo(() => {
    return availableUsers.filter((user) => {
      return (
        user.name.toLowerCase().includes(search.toLowerCase()) ||
        user.email.toLowerCase().includes(search.toLowerCase())
      );
    });
  }, [availableUsers, search]);

  const handleLink = async (user: GetUsersDto["data"][number]) => {
    await linkUser(user.uuid);
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="default" title="Impersonate" disabled={isLoading}>
          <FontAwesomeIcon icon={faAdd} />
          Add User
        </Button>
      </DialogTrigger>
      <DialogContent className="flex w-fit max-w-4xl min-w-sm flex-col">
        <DialogHeader>
          <DialogTitle>Add User to Group</DialogTitle>
        </DialogHeader>
        <Input
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="flex-1 overflow-y-auto">
          <GroupedTable
            data={[
              {
                groupId: "users",
                label: "Users",
                items: filteredUsers,
              },
            ]}
            columns={[
              {
                key: "name",
                label: "Name",
              },
              {
                key: "email",
                label: "Email",
              },
            ]}
            renderCell={{
              name: (_, row) => <UserDisplayName user={row} />,
              email: (value) =>
                String(value).length > truncateLength ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>{`${String(value).slice(0, truncateLength)}…`}</span>
                      </TooltipTrigger>
                      <TooltipContent>{String(value)}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  String(value)
                ),
              role: (value) => <span className="capitalize">{value}</span>,
            }}
            renderActions={({ row }) => (
              <Button size="sm" onClick={() => handleLink(row)}>
                <FontAwesomeIcon icon={faAdd} />
              </Button>
            )}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
